<template>
    <ButtonComp
        v-if="props.buttonOnly && !isSearchFocused"
        buttonType="Secondary"
        @click="focusOnSearch"
        icon="fa-search"
        :class="[props.small ? 'btn-sm' : '']"
    />
    <TypeAhead
        v-else
        ref="typeahead"
        v-model:model-value="value"
        :class="cssClasses"
        :datasources="[datasource]"
        :displayItemComponent="EventNameDateIdTypeAheadLine"
        :ignore-minvalue-on-integer="true"
        :autoresize="props.autoresize"
        :placeholder="props.placeholder ?? (isSearchFocused ? 'general.searcheventcontestants' : 'general.search')"
        @item-selected="emit('item-selected', $event)"
        @focus="isSearchFocused = true"
        @blur="isSearchFocused = false"
    >
    </TypeAhead>
</template>
<script setup lang="ts">
import { ButtonComp, EventNameDateIdTypeAheadLine, IconComp, TypeAhead } from '@/components';
import { computed, ref, useAttrs, nextTick } from 'vue';
import { EventDao } from '@/dao';
import { EventinfoBase, TypeAheadItem, TypeAheadResponse } from '@/objects/generated';
import { TypeAheadDataSource, type TypeAheadExpose } from '@/components/Typeahead';
import { Router } from '@/objects/Router';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
    defineProps<{
        /**
         * If the typeahead should only be a button
         * Will expand when clicked
         * @default false
         */
        buttonOnly?: boolean;

        /**
         * If the typeahead should be small
         * @default false
         */
        small?: boolean;

        /**
         * Override the placeholder
         * @default undefined
         */
        placeholder?: string;

        /**
         * If the typeahead should automatically resize to the width of the content. Only applies when not buttonOnly
         * @default false
         */
        autoresize?: boolean;

        /**
         * Contains a list of events that will be available without any filter. This are the most likely events
         */
        eventlist?: EventinfoBase[] | null;
    }>(),
    {
        buttonOnly: false,

        small: false,
    },
);

// If the search filed is focused or not
const isSearchFocused = ref(false);

// The value of the search
const value = ref<string>('');

// The typeahead component
const typeahead = ref<TypeAheadExpose | null>(null);

/**
 * Change the focus from the button to the search
 */
const focusOnSearch = () => {
    isSearchFocused.value = true;
    nextTick(() => {
        typeahead.value?.focus();
    });
};

// useAttrs can only be called in setupfunction
const attrs = useAttrs();

/**
 * The sum of the classes from the $attrs and the focused class
 */
const cssClasses = computed(() => {
    // Combine the attrs class with the focused class
    return [attrs.class, { 'form-control-sm': props.small }, { 'is-focused': isSearchFocused.value }];
});

/**
 * We need to return the events as typeahead items. This is the query function for the typeahead datasource
 */
let typeaheadEvents: TypeAheadItem[] | null = null;

if (props.eventlist && props.eventlist.length > 0) {
    typeaheadEvents = [];
    typeaheadEvents.push(
        ...props.eventlist.map(
            (x) => ({ ID: x.ID, DataType: 'Event', Text: x.Name, Description: x.Date.toLocaleDateString() }) as TypeAheadItem,
        ),
    );
}

/**
 * The datasource for the typeahead
 */
const datasource = new TypeAheadDataSource(
    'Contains',
    async (request) => {
        const response = TypeAheadResponse.FromJsValues({
            Items: await EventDao.TypeaheadSearch(request),
        });
        return response;
    },
    undefined,
    undefined,
    (item: TypeAheadItem) => Router.GetRouteFromNameAndParams('eventinfo', { eventID: item.ID })?.href,
);
datasource.StickyItems = typeaheadEvents;

defineExpose<TypeAheadExpose>({ focus: () => typeahead.value?.focus() });

const emit = defineEmits<{
    (e: 'item-selected', item: TypeAheadItem): void;
}>();
</script>
